import { Link, Typography } from "@mui/material";
import { useState } from "react";
import { ItemProperty, MaterialSymbolIcon, MaterialSymbols, UserAutocomplete } from "src/components";
import { IssueDto, useEditIssueDecisionMaker, type UserDto } from "../../http";
import { Links } from "src/constants";

interface IProps {
  issue: IssueDto;
}

const IssueDecisionMakerProperty = (props: IProps) => {
  const { mutateAsync, isLoading: isEditingIssue } = useEditIssueDecisionMaker();
  const [isEditing, setIsEditing] = useState(false);
  const [optimisticDecisionMaker, setOptimisticDecisionMaker] = useState<UserDto | null | undefined>(undefined);
  const displayDecisionMaker =
    optimisticDecisionMaker !== undefined ? optimisticDecisionMaker : (props.issue.decisionMaker ?? null);

  async function handleDecisionMakerChanged(user: UserDto | null) {
    setOptimisticDecisionMaker(user);

    try {
      await mutateAsync({
        issueGuid: props.issue.guid,
        teamSlug: props.issue.assignedTeam?.slug,
        decisionMakerUserId: user?.userId ?? undefined,
        decisionMaker: user,
        createdByUserId: props.issue.createdByUser.userId,
      });

      setIsEditing(false);
    } finally {
      //clear the local state since we would rather show the value from the server.
      setOptimisticDecisionMaker(undefined);
    }
  }

  return (
    <ItemProperty
      labelIcon={<MaterialSymbolIcon symbol={MaterialSymbols.Gavel} />}
      label="Decision Maker"
      canEdit={props.issue.permissions.userCanEditDecisionMaker}
      isEditing={isEditing}
      onEditClicked={() => setIsEditing(true)}
    >
      {isEditing ? (
        <UserAutocomplete
          autofocus
          openOnFocus
          onChange={handleDecisionMakerChanged}
          onBlur={() => setIsEditing(false)}
          value={displayDecisionMaker}
          placeholder="Assign a decision maker..."
          disabled={props.issue.permissions.userCanEditDecisionMaker === false || isEditingIssue}
        />
      ) : displayDecisionMaker ? (
        <Link href={Links.User(displayDecisionMaker.userId)}>{displayDecisionMaker.displayName}</Link>
      ) : (
        <Typography variant="placeholder">Assign a decision maker...</Typography>
      )}
    </ItemProperty>
  );
};

export { IssueDecisionMakerProperty };
